import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Lock,
  Mail,
  Eye,
  EyeOff,
  ArrowRight,
  Car,
  Users,
  BarChart3,
  ShieldCheck,
  Link2,
} from "lucide-react";
import { Script } from "@/components/Script";
import { CONTACT, IMAGES } from "@/config";
import { supabase } from "@/lib/supabase";

const features = [
  { icon: Car, title: "Manage Vehicle Listings", desc: "Add, edit and update your stock." },
  { icon: Users, title: "Track Leads & Enquiries", desc: "Stay on top of every opportunity." },
  { icon: BarChart3, title: "Keep Your Content Fresh", desc: "Showcase the latest vehicles." },
];

const trust = [
  { icon: ShieldCheck, title: "Secure Access", desc: "Your data is protected with industry-standard security." },
  { icon: Lock, title: "Your Privacy Matters", desc: "We keep your information safe and confidential." },
  { icon: Users, title: "Authorised Use Only", desc: "This system is for authorised personnel only." },
];

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter your email and password.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    const { data: isAdmin } = await supabase.rpc("is_inventory_admin");
    if (!isAdmin) {
      await supabase.auth.signOut();
      toast.error("This account is not authorised to manage inventory.");
      return;
    }
    toast.success("Signed in successfully.");
    navigate("/admin");
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-blue-50/60 to-white">
      {/* Top bar */}
      <header className="border-b border-slate-100 bg-white/90 backdrop-blur">
        <div className="container-x flex h-[72px] items-center justify-between">
          <Link to="/" className="flex items-center gap-3 leading-tight">
            <img src={IMAGES.brandLogo} alt="" className="h-11 w-11 rounded-xl object-contain" width="44" height="44" />
            <div>
              <div className="text-xl font-extrabold text-slate-900">
                Simphiwe <span className="text-red-600">Fudwana</span>
              </div>
              <div className="text-[11px] font-medium text-slate-500">{CONTACT.role}</div>
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-600">
            <Car className="h-4 w-4" /> Back to Website <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <main className="container-x flex-1 py-10">
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            <Lock className="h-3.5 w-3.5" /> Secure Admin Access
          </span>
          <h1 className="mt-4 text-4xl font-extrabold text-slate-900 sm:text-5xl">
            Welcome Back, <span className="brand-blue">Admin Login</span>
          </h1>
          <p className="mt-3 max-w-xl text-sm text-slate-500">
            Access your dashboard to manage vehicle listings, leads and enquiries.
          </p>
        </div>

        <div className="grid items-start gap-8 lg:grid-cols-2">
          {/* Login form */}
          <div className="card-soft p-6 sm:p-8">
            <h2 className="text-xl font-bold text-slate-900">Admin Login</h2>
            <p className="mt-1 text-sm text-slate-500">
              Sign in to your admin dashboard to manage your listings, leads and website content.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="sfudwana@cfaomotors.co.za"
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    data-testid="admin-email"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-10 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    data-testid="admin-password"
                  />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Toggle password">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                  Remember me
                </label>
                <Link to="/admin/forgot-password" className="text-sm font-semibold text-blue-600 hover:underline" data-testid="login-forgot-password-link">Forgot password?</Link>
              </div>
              <button type="submit" disabled={submitting} className="btn-blue w-full" data-testid="admin-signin">
                {submitting ? "Please wait..." : "Sign In to Dashboard"} <ArrowRight className="h-4 w-4" />
              </button>

              <p className="text-center text-sm text-slate-500">
                Need an authorised admin account?{" "}
                <Link to="/admin/register" className="font-semibold text-blue-600 hover:underline" data-testid="login-register-link">Register</Link>
              </p>

              <div className="flex items-center gap-3 py-1">
                <span className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-400">Or</span>
                <span className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="flex w-full items-center justify-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700">
                <Link2 className="h-4 w-4" /> Use a secure connection
              </div>
              <p className="flex items-center justify-center gap-1.5 text-center text-xs text-slate-400">
                <Lock className="h-3.5 w-3.5" /> This admin area is restricted and secure. Only authorised personnel may access this system.
              </p>
            </form>
          </div>

          {/* Promo */}
          <div className="rounded-2xl bg-slate-900 p-8 text-white">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-300">Manage. Update. Grow.</p>
            <h2 className="mt-3 text-3xl font-extrabold">Your Business In One Place</h2>
            <p className="mt-3 text-sm text-slate-300">
              Keep your vehicle listings up to date, respond to new leads, and deliver an exceptional
              customer experience.
            </p>
            <div className="mt-7 space-y-4">
              {features.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-blue-300">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-sm font-bold">{f.title}</h3>
                      <p className="text-xs text-slate-400">{f.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 text-right">
              <Script color="#ffffff" swooshColor="#60a5fa" size="text-2xl">Driven By People</Script>
            </div>
          </div>
        </div>

        {/* Trust cards */}
        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          {trust.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.title} className="card-soft p-6 text-center">
                <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-3 text-sm font-bold text-slate-900">{t.title}</h3>
                <p className="mt-1.5 text-xs text-slate-500">{t.desc}</p>
              </div>
            );
          })}
        </div>
      </main>

      <footer className="border-t border-slate-100 bg-white py-6">
        <div className="container-x flex flex-col items-center justify-between gap-3 text-xs text-slate-400 sm:flex-row">
          <span>© 2024 Simphiwe Fudwana. All rights reserved.</span>
          <Script size="text-xl">Good People, Great Journeys</Script>
        </div>
      </footer>
    </div>
  );
};

export default AdminLogin;
